/**
 * 게시판 서비스 — Supabase CRUD
 *
 * - Firebase Auth JWT는 SupabaseClient가 자동 주입
 * - RLS 정책에 의해 워크스페이스 멤버만 접근 가능
 */
import { supabase } from './SupabaseClient';
import type { TablesInsert, TablesUpdate } from '../types/supabase';
import {
  Board, Post, Comment, PostAuthor, BoardCategory,
  rowToBoard, rowToPost, rowToComment, rowToBoardCategory,
  appendStatusChange,
} from '../models/Board';
import type { TaskStatus } from '../models/Board';
import { getFunc } from './FireClient';

function getClient() {
  if (!supabase) throw new Error('[BoardService] Supabase 클라이언트가 초기화되지 않았습니다.');
  return supabase;
}

// ═══════════════════════════════
//  Boards
// ═══════════════════════════════

/** 워크스페이스의 모든 게시판 조회 (archived 제외, tasks는 채널 인라인에서만 노출) */
export async function fetchBoards(workspaceId: string): Promise<Board[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('boards')
    .select('*')
    .eq('workspace_id', workspaceId)
    .is('archived_at', null)
    .neq('type', 'tasks')
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(rowToBoard);
}

/** 게시판 생성 */
export async function createBoard(params: {
  workspace_id: string;
  name: string;
  description?: string;
  type?: string;
  scope?: string;
  channel_id?: string;
  owner_id: string;
  admin_ids?: string[];
  write_permission?: string;
  comment_permission?: string;
  notify_all_permission?: string;
  min_role?: string | null;
}): Promise<Board> {
  const sb = getClient();
  const insertData: TablesInsert<'boards'> = {
    workspace_id: params.workspace_id,
    name: params.name,
    description: params.description ?? null,
    type: params.type ?? 'general',
    scope: params.scope ?? 'workspace',
    channel_id: params.channel_id ?? null,
    owner_id: params.owner_id,
  };
  if (params.admin_ids) insertData.admin_ids = params.admin_ids;
  if (params.write_permission) insertData.write_permission = params.write_permission;
  if (params.comment_permission) insertData.comment_permission = params.comment_permission;
  if (params.notify_all_permission) insertData.notify_all_permission = params.notify_all_permission;
  if (params.min_role !== undefined) insertData.min_role = params.min_role;

  const { data, error } = await sb
    .from('boards')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return rowToBoard(data);
}

/** 게시판 수정 */
export async function updateBoard(
  boardId: string,
  updates: {
    name?: string;
    description?: string;
    type?: string;
    display_style?: string | null;
    admin_ids?: string[];
    write_permission?: string;
    comment_permission?: string;
    notify_all_permission?: string;
    min_role?: string | null;
  }
): Promise<Board> {
  const sb = getClient();
  const { data, error } = await sb
    .from('boards')
    .update(updates)
    .eq('id', boardId)
    .select()
    .single();

  if (error) throw error;
  return rowToBoard(data);
}

/** 게시판 아카이브 (소프트 삭제) */
export async function archiveBoard(boardId: string, reason?: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('boards')
    .update({ archived_at: new Date().toISOString(), archived_reason: reason ?? null })
    .eq('id', boardId);

  if (error) throw error;
}

// ═══════════════════════════════
//  Posts
// ═══════════════════════════════

/** 게시판의 게시글 목록 (페이지네이션) */
export async function fetchPosts(
  boardId: string,
  options?: {
    page?: number;
    pageSize?: number;
    tag?: string;
    authorUid?: string;
    /** 위키 게시판: 특정 부모의 자식 페이지만 조회 (null = 루트 페이지만) */
    parentPostId?: string | null;
    /** 정렬 방식 (기본: 고정 우선 → created_at 내림차순) */
    sort?: 'created_desc' | 'updated_desc';
  }
): Promise<{ posts: Post[]; total: number }> {
  const sb = getClient();
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 20;
  const from = page * pageSize;
  const to = from + pageSize - 1;

  let query = sb
    .from('posts')
    .select('*', { count: 'exact' })
    .eq('board_id', boardId);

  if (options?.sort === 'updated_desc') {
    query = query
      .order('updated_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false });
  } else {
    query = query
      .order('is_pinned', { ascending: false })
      .order('created_at', { ascending: false });
  }
  query = query.range(from, to);

  if (options?.tag) {
    query = query.contains('tags', [options.tag]);
  }
  if (options?.authorUid) {
    query = query.eq('author->>uid', options.authorUid);
  }
  // parentPostId: undefined = 필터 없음, null = 루트 페이지만, string = 해당 부모의 자식만
  if (options?.parentPostId === null) {
    query = query.is('parent_post_id', null);
  } else if (typeof options?.parentPostId === 'string') {
    query = query.eq('parent_post_id', options.parentPostId);
  }

  const { data, error, count } = await query;
  if (error) throw error;
  return {
    posts: (data ?? []).map(rowToPost),
    total: count ?? 0,
  };
}

/**
 * 위키 게시판의 전체 페이지 트리 조회.
 * 모든 페이지를 한 번에 가져와 클라이언트에서 parent_post_id 기준으로 트리 빌드.
 * 위키 게시판은 보통 페이지 수가 많지 않으므로 client-side 트리 빌드가 효율적.
 */
export async function fetchPageTree(boardId: string): Promise<Post[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('posts')
    .select('*')
    .eq('board_id', boardId)
    // 같은 부모 안에서 tree_sort_order 가 우선. 동일 값일 때 created_at 으로 안정 정렬.
    .order('tree_sort_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(rowToPost);
}

/** 같은 parent_post_id 안 위키 페이지 순서 일괄 갱신 — orderedIds 순서대로 0,1,2,... */
export async function reorderWikiPages(orderedIds: string[]): Promise<void> {
  if (orderedIds.length === 0) return;
  const sb = getClient();
  // 개별 update 병렬. dataset 작아서 충분 (한 부모 안 형제 reorder).
  await Promise.all(orderedIds.map((id, idx) =>
    sb.from('posts').update({ tree_sort_order: idx }).eq('id', id)
  ));
}

/**
 * TipTap JSON에서 위키링크(`@page:`) Mention 노드의 페이지 ID들을 추출.
 * 본문 저장 시 metadata.linked_page_ids에 캐싱하여 백링크 쿼리에 활용.
 */
export function extractLinkedPageIds(content: any): string[] {
  if (!content || typeof content !== 'object') return [];
  const ids = new Set<string>();
  const walk = (node: any) => {
    if (!node || typeof node !== 'object') return;
    if (node.type === 'mention' && node.attrs?.mentionType === 'wiki-page' && node.attrs?.id) {
      ids.add(String(node.attrs.id));
    }
    if (Array.isArray(node.content)) {
      node.content.forEach(walk);
    }
  };
  walk(content);
  return Array.from(ids);
}

/** 게시글 검색 (제목 + 본문 ilike) */
export async function searchPosts(
  boardId: string,
  query: string,
  options?: { page?: number; pageSize?: number }
): Promise<{ posts: Post[]; total: number }> {
  const sb = getClient();
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 20;
  const from = page * pageSize;
  const to = from + pageSize - 1;
  const pattern = `%${query}%`;

  const { data, error, count } = await sb
    .from('posts')
    .select('*', { count: 'exact' })
    .eq('board_id', boardId)
    .or(`title.ilike.${pattern},plain_text.ilike.${pattern}`)
    .order('created_at', { ascending: false })
    .range(from, to);

  if (error) throw error;
  return {
    posts: (data ?? []).map(rowToPost),
    total: count ?? 0,
  };
}

/** 게시글 상세 조회 */
export async function fetchPost(postId: string): Promise<Post | null> {
  const sb = getClient();
  const { data, error } = await sb
    .from('posts')
    .select('*')
    .eq('id', postId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // not found
    throw error;
  }
  return rowToPost(data);
}

/** 게시글 작성 */
export async function createPost(params: {
  board_id: string;
  workspace_id: string;
  title: string;
  content?: any;
  plain_text?: string;
  author: PostAuthor;
  tags?: string[];
  is_pinned?: boolean;
  is_announcement?: boolean;
  metadata?: Record<string, any>;
  parent_post_id?: string | null;
  mentioned_user_ids?: string[];
  mention_all?: boolean;
  attachments?: import('../models/Board').PostAttachment[];
}): Promise<Post> {
  const sb = getClient();
  // 본문에서 위키링크 페이지 ID 추출 → metadata.linked_page_ids 자동 캐싱
  const linkedPageIds = extractLinkedPageIds(params.content);
  const mergedMetadata: Record<string, any> = { ...(params.metadata ?? {}) };
  if (linkedPageIds.length > 0) {
    mergedMetadata.linked_page_ids = linkedPageIds;
  }
  const { data, error } = await sb
    .from('posts')
    .insert({
      board_id: params.board_id,
      workspace_id: params.workspace_id,
      title: params.title,
      content: params.content ?? null,
      plain_text: params.plain_text ?? null,
      author: params.author as any,
      tags: params.tags ?? null,
      is_pinned: params.is_pinned ?? false,
      is_announcement: params.is_announcement ?? false,
      metadata: Object.keys(mergedMetadata).length > 0 ? mergedMetadata : null,
      parent_post_id: params.parent_post_id ?? null,
      mentioned_user_ids: params.mentioned_user_ids ?? null,
      mention_all: params.mention_all === true,
      attachments: (params.attachments ?? []) as any,
    })
    .select()
    .single();

  if (error) throw error;
  return rowToPost(data);
}

/** 게시글 수정 (기존 내용을 revision으로 자동 저장) */
export async function updatePost(
  postId: string,
  updates: {
    title?: string;
    content?: any;
    plain_text?: string;
    tags?: string[];
    is_pinned?: boolean;
    is_announcement?: boolean;
    metadata?: Record<string, any> | null;
    parent_post_id?: string | null;
    mentioned_user_ids?: string[] | null;
    attachments?: import('../models/Board').PostAttachment[];
  }
): Promise<Post> {
  const sb = getClient();

  // 제목이나 본문이 변경되면 기존 내용을 revision으로 저장
  if (updates.title !== undefined || updates.content !== undefined) {
    try {
      // 현재 게시글 조회
      const { data: currentPost } = await sb
        .from('posts')
        .select('title, content, plain_text, author')
        .eq('id', postId)
        .single();

      if (currentPost) {
        // 다음 revision 번호 계산
        const { count } = await sb
          .from('post_revisions')
          .select('*', { count: 'exact', head: true })
          .eq('post_id', postId);

        await sb.from('post_revisions').insert({
          post_id: postId,
          revision_number: (count ?? 0) + 1,
          title: currentPost.title || '',
          content: currentPost.content || {},
          plain_text: currentPost.plain_text || '',
          edited_by: (currentPost.author as { uid?: string } | null)?.uid ?? '',
        });
      }
    } catch (err) {
      // revision 저장 실패해도 게시글 수정은 계속 진행
      console.warn('[BoardService] revision 저장 실패:', err);
    }
  }

  // content 변경 시: 위키링크 페이지 ID 재계산하여 metadata.linked_page_ids 갱신
  // (호출자가 명시적으로 metadata를 넘긴 경우엔 그것 위에 병합)
  const mergedUpdates: Record<string, any> = { ...updates, updated_at: new Date().toISOString() };
  if (updates.content !== undefined) {
    const linkedPageIds = extractLinkedPageIds(updates.content);
    // 기존 metadata를 보존한 채 linked_page_ids 만 덮어쓰기
    const { data: cur } = await sb
      .from('posts')
      .select('metadata')
      .eq('id', postId)
      .single();
    const base: Record<string, any> = updates.metadata !== undefined
      ? (updates.metadata ?? {})
      : { ...((cur?.metadata as Record<string, any> | null) ?? {}) };
    if (linkedPageIds.length > 0) {
      base.linked_page_ids = linkedPageIds;
    } else {
      delete base.linked_page_ids;
    }
    mergedUpdates.metadata = Object.keys(base).length > 0 ? base : null;
  }

  const { data, error } = await sb
    .from('posts')
    .update(mergedUpdates as TablesUpdate<'posts'>)
    .eq('id', postId)
    .select()
    .single();

  if (error) throw error;
  return rowToPost(data);
}

// ═══════════════════════════════
//  Revisions (수정 이력)
// ═══════════════════════════════

/** 게시글 수정 이력 조회 (최신순) */
export async function fetchPostRevisions(postId: string) {
  const sb = getClient();
  const { data, error } = await sb
    .from('post_revisions')
    .select('*')
    .eq('post_id', postId)
    .order('revision_number', { ascending: false });

  if (error) throw error;
  return data ?? [];
}

// ═══════════════════════════════
//  Board Pins (게시판 고정/즐겨찾기)
// ═══════════════════════════════

/** 고정된 게시판 ID 목록 조회 (pinned_at 순) */
export async function getPinnedBoardIds(userId: string): Promise<string[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('user_board_pins')
    .select('board_id')
    .eq('user_id', userId)
    .order('pinned_at', { ascending: true });

  if (error) {
    console.warn('[BoardService] getPinnedBoardIds 실패:', error);
    return [];
  }
  return (data ?? []).map(r => r.board_id);
}

/** 게시판 고정 토글 (고정 ↔ 해제) */
export async function toggleBoardPin(boardId: string, userId: string): Promise<boolean> {
  const sb = getClient();

  const { data: existing } = await sb
    .from('user_board_pins')
    .select('board_id')
    .eq('user_id', userId)
    .eq('board_id', boardId)
    .maybeSingle();

  if (existing) {
    const { error } = await sb
      .from('user_board_pins')
      .delete()
      .eq('user_id', userId)
      .eq('board_id', boardId);
    if (error) console.warn('[BoardService] unpinBoard 실패:', error);
    return false; // unpinned
  } else {
    const { error } = await sb
      .from('user_board_pins')
      .insert({ user_id: userId, board_id: boardId });
    if (error) console.warn('[BoardService] pinBoard 실패:', error);
    return true; // pinned
  }
}

/** 게시판에서 사용된 태그 목록 조회 (고유값, 빈도순) */
export async function getUsedTags(boardId: string): Promise<string[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('posts')
    .select('tags')
    .eq('board_id', boardId)
    .not('tags', 'is', null);

  if (error) {
    console.warn('[BoardService] getUsedTags 실패:', error);
    return [];
  }

  // 태그 빈도 집계
  const freq = new Map<string, number>();
  (data ?? []).forEach(row => {
    (row.tags as string[] | null)?.forEach(tag => {
      freq.set(tag, (freq.get(tag) ?? 0) + 1);
    });
  });

  // 빈도 내림차순 정렬
  return Array.from(freq.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([tag]) => tag);
}

export interface TagFrequency {
  tag: string;
  count: number;
}

/**
 * 태그 빈도 집계 (승격 추천용)
 * default_tags에 포함되지 않은 자유 태그 중 minCount 이상 사용된 태그 반환
 */
export async function getPromotableTags(
  boardId: string,
  defaultTags: string[],
  minCount: number = 3,
): Promise<TagFrequency[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('posts')
    .select('tags')
    .eq('board_id', boardId)
    .not('tags', 'is', null);

  if (error) {
    console.warn('[BoardService] getPromotableTags 실패:', error);
    return [];
  }

  const defaultSet = new Set(defaultTags);
  const freq = new Map<string, number>();
  (data ?? []).forEach(row => {
    (row.tags as string[] | null)?.forEach(tag => {
      if (!defaultSet.has(tag)) {
        freq.set(tag, (freq.get(tag) ?? 0) + 1);
      }
    });
  });

  return Array.from(freq.entries())
    .filter(([, count]) => count >= minCount)
    .sort((a, b) => b[1] - a[1])
    .map(([tag, count]) => ({ tag, count }));
}

/** 태그를 게시판 default_tags에 승격 (추가) */
export async function promoteTagToDefault(boardId: string, tag: string): Promise<void> {
  const sb = getClient();
  // 현재 default_tags 조회
  const { data: board } = await sb
    .from('boards')
    .select('default_tags')
    .eq('id', boardId)
    .single();

  const currentTags: string[] = board?.default_tags ?? [];
  if (currentTags.includes(tag)) return; // 이미 포함

  const { error } = await sb
    .from('boards')
    .update({ default_tags: [...currentTags, tag] })
    .eq('id', boardId);

  if (error) console.warn('[BoardService] promoteTagToDefault 실패:', error);
}

/** 게시글 수정 이력 건수 */
export async function fetchRevisionCount(postId: string): Promise<number> {
  const sb = getClient();
  const { count, error } = await sb
    .from('post_revisions')
    .select('*', { count: 'exact', head: true })
    .eq('post_id', postId);

  if (error) throw error;
  return count ?? 0;
}

/** 게시글 삭제 */
export async function deletePost(postId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb.from('posts').delete().eq('id', postId);
  if (error) throw error;
}

/** 조회수 증가 (RPC) */
export async function incrementViewCount(postId: string, userId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb.rpc('increment_post_view_count', {
    p_post_id: postId,
    p_user_id: userId,
  });
  if (error) console.warn('[BoardService] 조회수 증가 실패:', error.message);
}

/**
 * 반응 토글 공통 — content_reactions 테이블에 자기 행만 기록.
 * posts/comments.reactions jsonb 는 DB 트리거가 재집계하는 파생 캐시
 * (부모 행 직접 UPDATE 는 author-only RLS 라 타인 글에 반응 불가했음 — QA-11).
 * PK (target_type, target_id, user_id) 가 "한 유저당 1개 리액션" 규칙을 보장.
 */
async function toggleContentReaction(
  targetType: 'post' | 'comment',
  targetId: string,
  userId: string,
  reactionType: string,
): Promise<void> {
  const sb = getClient();

  // 내 기존 반응 확인 (RLS: 자기 행만 조회 가능)
  const { data: mine, error: fetchErr } = await sb
    .from('content_reactions')
    .select('emoji')
    .eq('target_type', targetType)
    .eq('target_id', targetId)
    .eq('user_id', userId)
    .maybeSingle();
  if (fetchErr) throw fetchErr;

  if (mine?.emoji === reactionType) {
    // 같은 리액션 재클릭 → 제거 (토글 off)
    const { error } = await sb
      .from('content_reactions')
      .delete()
      .eq('target_type', targetType)
      .eq('target_id', targetId)
      .eq('user_id', userId);
    if (error) throw error;
  } else {
    // 새 리액션 또는 다른 이모지로 교체
    const { error } = await sb.from('content_reactions').upsert(
      { target_type: targetType, target_id: targetId, user_id: userId, emoji: reactionType },
      { onConflict: 'target_type,target_id,user_id' },
    );
    if (error) throw error;
  }
}

/** 리액션 토글 (채팅 스타일: smile, thumbs-up, heart, check, shake-hand) */
export async function toggleReaction(
  postId: string,
  userId: string,
  reactionType: string,
): Promise<Post> {
  await toggleContentReaction('post', postId, userId, reactionType);

  // 트리거가 갱신한 집계(reactions/liked_by/like_count) 포함 재조회
  const { data, error } = await getClient()
    .from('posts')
    .select()
    .eq('id', postId)
    .single();

  if (error) throw error;
  return rowToPost(data);
}

/** 댓글 리액션 토글 (한 유저당 1개 리액션만 허용) */
export async function toggleCommentReaction(
  commentId: string,
  userId: string,
  reactionType: string,
): Promise<Comment> {
  await toggleContentReaction('comment', commentId, userId, reactionType);

  const { data, error } = await getClient()
    .from('comments')
    .select()
    .eq('id', commentId)
    .single();

  if (error) throw error;
  return rowToComment(data);
}

// wiki_categories 관련 함수 폐기 — 위키 페이지 분류는 parent_post_id 계층으로 통합.
// 테이블·post.category_id 컬럼은 데이터 보존 위해 남겨두지만 코드에서는 사용 안 함.

// ═══════════════════════════════
//  QnA — 답변 채택
// ═══════════════════════════════

/** QnA 답변 채택 — 게시글의 metadata에 해결 상태 + 채택 댓글 ID 저장 */
export async function acceptAnswer(postId: string, commentId: string): Promise<Post> {
  return updatePost(postId, {
    metadata: { is_resolved: true, accepted_comment_id: commentId },
  });
}

/** QnA 채택 취소 — 미해결 상태로 복원 */
export async function unresolveQuestion(postId: string): Promise<Post> {
  return updatePost(postId, {
    metadata: { is_resolved: false, accepted_comment_id: null },
  });
}

// ═══════════════════════════════
//  Issue — 경량 업무 추적
// ═══════════════════════════════

/** 이슈 상태 변경 */
export async function updateIssueStatus(
  postId: string,
  currentMetadata: Record<string, any> | null,
  status: string,
): Promise<Post> {
  const newMeta = { ...(currentMetadata ?? {}), status };
  return updatePost(postId, { metadata: newMeta });
}

/** 이슈 담당자 배정/해제 */
export async function updateIssueAssignee(
  postId: string,
  currentMetadata: Record<string, any> | null,
  assignee: { uid: string; name: string; photoUrl?: string | null } | null,
): Promise<Post> {
  const newMeta = { ...(currentMetadata ?? {}), assignee };
  return updatePost(postId, { metadata: newMeta });
}

/** 이슈 마감일 설정/해제 */
export async function updateIssueDueDate(
  postId: string,
  currentMetadata: Record<string, any> | null,
  dueDate: string | null,
): Promise<Post> {
  const newMeta = { ...(currentMetadata ?? {}), due_date: dueDate };
  return updatePost(postId, { metadata: newMeta });
}

/** 이슈 우선순위 변경 */
export async function updateIssuePriority(
  postId: string,
  currentMetadata: Record<string, any> | null,
  priority: string,
): Promise<Post> {
  const newMeta = { ...(currentMetadata ?? {}), priority };
  return updatePost(postId, { metadata: newMeta });
}

// ═══════════════════════════════
//  Comments
// ═══════════════════════════════

/** 게시글의 댓글 목록 */
export async function fetchComments(postId: string): Promise<Comment[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('comments')
    .select('*')
    .eq('target_id', postId)
    .eq('target_type', 'post')
    .is('parent_comment_id', null) // 최상위 댓글만
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(rowToComment);
}

/** 대댓글 조회 */
export async function fetchReplies(parentCommentId: string): Promise<Comment[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('comments')
    .select('*')
    .eq('parent_comment_id', parentCommentId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data ?? []).map(rowToComment);
}

/** 댓글 작성 */
export async function createComment(params: {
  target_id: string;
  target_type: 'post' | 'event';
  text: string;
  author: PostAuthor;
  /** 쓰레드 루트 댓글 id. null/undefined면 새 쓰레드 (최상위 댓글). */
  parent_comment_id?: string;
  /** 같은 쓰레드 내에서 어떤 형제 답글에 답한 것인지 (인용 대상). */
  reply_to_comment_id?: string;
  mentioned_user_ids?: string[];
  /** 첨부파일 (로컬 업로드 + 채팅 파일 참조) */
  attachments?: import('../models/Board').CommentAttachment[];
}): Promise<Comment> {
  const sb = getClient();
  const { data, error } = await sb
    .from('comments')
    .insert({
      target_id: params.target_id,
      target_type: params.target_type,
      text: params.text,
      author: params.author as any,
      parent_comment_id: params.parent_comment_id ?? null,
      reply_to_comment_id: params.reply_to_comment_id ?? null,
      mentioned_user_ids: params.mentioned_user_ids?.length ? params.mentioned_user_ids : null,
      attachments: params.attachments?.length ? (params.attachments as any) : [],
    })
    .select()
    .single();

  if (error) throw error;
  return rowToComment(data);
}

/** 게시글 첨부파일 업로드 — board-attachments 버킷의 posts prefix 사용.
 *  comments 의 uploadCommentAttachment 와 동일 패턴. group 은 mime 으로 image/doc 분류. */
export async function uploadPostAttachment(
  file: File,
  userId: string,
  boardId: string,
): Promise<{ url: string; filename: string; sizeByte: number; group: 'image' | 'doc' }> {
  const sb = getClient();
  const ext = file.name.split('.').pop() || 'bin';
  const safeName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const filePath = `${userId}/posts/${boardId}/${safeName}`;

  const { error } = await sb.storage
    .from('board-attachments')
    .upload(filePath, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;

  const { data } = sb.storage.from('board-attachments').getPublicUrl(filePath);
  const group: 'image' | 'doc' = file.type.startsWith('image/') ? 'image' : 'doc';
  return { url: data.publicUrl, filename: file.name, sizeByte: file.size, group };
}

/** 댓글 첨부파일 업로드 — board-attachments 버킷의 별도 prefix 사용 */
export async function uploadCommentAttachment(
  file: File,
  userId: string,
  postId: string,
): Promise<{ url: string; filename: string; sizeByte: number; group: 'image' | 'doc' }> {
  const sb = getClient();
  const ext = file.name.split('.').pop() || 'bin';
  const safeName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const filePath = `${userId}/comments/${postId}/${safeName}`;

  const { error } = await sb.storage
    .from('board-attachments')
    .upload(filePath, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;

  const { data } = sb.storage.from('board-attachments').getPublicUrl(filePath);
  const group: 'image' | 'doc' = file.type.startsWith('image/') ? 'image' : 'doc';
  return { url: data.publicUrl, filename: file.name, sizeByte: file.size, group };
}

/** 댓글 텍스트 수정 (작성자만 가능) */
export async function updateComment(commentId: string, text: string): Promise<Comment> {
  const sb = getClient();
  const { data, error } = await sb
    .from('comments')
    .update({ text })
    .eq('id', commentId)
    .select()
    .single();

  if (error) throw error;
  return rowToComment(data);
}

/** 댓글 삭제 */
export async function deleteComment(commentId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb.from('comments').delete().eq('id', commentId);
  if (error) throw error;
}

// ═══════════════════════════════
//  Storage (첨부파일)
// ═══════════════════════════════

/** 게시판 이미지 업로드 → public URL 반환 */
export async function uploadBoardImage(
  file: File,
  userId: string,
  boardId: string,
): Promise<string> {
  const sb = getClient();
  const ext = file.name.split('.').pop() || 'png';
  const fileName = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const filePath = `${userId}/${boardId}/${fileName}`;

  const { error } = await sb.storage
    .from('board-attachments')
    .upload(filePath, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (error) throw error;

  const { data } = sb.storage
    .from('board-attachments')
    .getPublicUrl(filePath);

  return data.publicUrl;
}

// ═══════════════════════════════
//  Notifications (알림)
// ═══════════════════════════════

/** 신규 게시글 작성 알림 전송 (수신자별 알림 prefs 기반 필터링 — functions board-notify) */
export async function notifyNewPost(
  workspaceId: string,
  boardId: string,
  boardName: string,
  postTitle: string,
  postId: string,
  mentionedUserIds?: string[],
  mentionAll?: boolean,
): Promise<void> {
  try {
    const fn = getFunc('notifyBoardPost');
    await fn({
      workspaceId,
      boardId,
      boardName,
      postTitle,
      postId,
      mentionedUserIds: mentionedUserIds ?? [],
      mentionAll: mentionAll === true,
    });
  } catch (err) {
    // 알림 실패가 게시글 작성 자체를 방해하면 안 됨
    console.warn('[BoardService] 알림 전송 실패:', err);
  }
}

/** 댓글 작성 알림 전송 (notify_comment 기반 필터링 + 멘션은 항상) */
export async function notifyNewComment(
  workspaceId: string,
  boardId: string,
  boardName: string,
  postTitle: string,
  postId: string,
  commentBody: string,
  mentionedUserIds?: string[],
  mentionAll?: boolean,
): Promise<void> {
  try {
    const fn = getFunc('notifyBoardPost');
    await fn({
      workspaceId,
      boardId,
      boardName,
      postTitle,
      postId,
      mentionedUserIds: mentionedUserIds ?? [],
      mentionAll: mentionAll === true,
      targetType: 'comment',
      commentBody,
    });
  } catch (err) {
    console.warn('[BoardService] 댓글 알림 전송 실패:', err);
  }
}

// ═══════════════════════════════
//  Tasks — 태스크 카드 관리
// ═══════════════════════════════

/**
 * 채널의 위키 게시판 조회. 동일 채널에 여러 개 있으면 가장 오래된 것을 반환.
 * (Phase 3 채팅→위키 변환 시 "채널 위키 존재 여부" 판별용)
 */
export async function fetchWikiBoardForChannel(channelId: string): Promise<Board | null> {
  const sb = getClient();
  const { data, error } = await sb
    .from('boards')
    .select('*')
    .eq('channel_id', channelId)
    .eq('type', 'wiki')
    .is('archived_at', null)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data ? rowToBoard(data) : null;
}

/** 채널의 Tasks 보드 조회 (type='tasks') */
export async function fetchTasksBoard(channelId: string): Promise<Board | null> {
  const sb = getClient();
  const { data, error } = await sb
    .from('boards')
    .select('*')
    .eq('channel_id', channelId)
    .eq('type', 'tasks')
    .is('archived_at', null)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data ? rowToBoard(data) : null;
}
/** @deprecated fetchInsightBoard → fetchTasksBoard */
export const fetchInsightBoard = fetchTasksBoard;

/** 태스크 카드 목록 조회 (상태 필터 + 정렬) */
export async function fetchTaskCards(
  boardId: string,
  options?: {
    status?: string;         // 'requested' | 'in_progress' | 'done' | undefined(전체)
    sort?: 'activity' | 'due_date' | 'created';
    page?: number;
    pageSize?: number;
  }
): Promise<{ posts: Post[]; total: number }> {
  const sb = getClient();
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? 50;
  const from = page * pageSize;
  const to = from + pageSize - 1;

  let query = sb
    .from('posts')
    .select('*', { count: 'exact' })
    .eq('board_id', boardId);

  // 상태 필터 (metadata JSONB에서 status 필드)
  if (options?.status) {
    query = query.eq('metadata->>status', options.status);
  }

  // 정렬
  switch (options?.sort) {
    case 'due_date':
      query = query.order('metadata->>due_date', { ascending: true, nullsFirst: false });
      break;
    case 'created':
      query = query.order('created_at', { ascending: false });
      break;
    case 'activity':
    default:
      query = query.order('metadata->>last_activity_at', { ascending: false });
      break;
  }

  query = query.range(from, to);

  const { data, error, count } = await query;
  if (error) throw error;
  return {
    posts: (data ?? []).map(rowToPost),
    total: count ?? 0,
  };
}
/** @deprecated fetchInsightCards → fetchTaskCards */
export const fetchInsightCards = fetchTaskCards;

/**
 * 특정 쓰레드 메시지에서 이미 태스크가 생성되었는지 확인.
 * 반드시 해당 채널의 '태스크 보드'에 한정한다 — 같은 source_thread_id 로 위키/일반 게시판에
 * 저장된 post(채팅 메시지를 위키로도 저장한 경우 등)를 태스크 중복으로 오판하지 않도록 board_id 로 필터링.
 */
export async function checkExistingTasksByThread(threadMessageId: string, channelId: string): Promise<Post[]> {
  const sb = getClient();
  const tasksBoard = await fetchTasksBoard(channelId);
  if (!tasksBoard) return [];

  const { data, error } = await sb
    .from('posts')
    .select('*')
    .eq('board_id', tasksBoard.id)
    .eq('metadata->>source_thread_id', threadMessageId);

  if (error) {
    console.warn('[BoardService] 중복 태스크 검사 실패:', error.message);
    return [];
  }
  return (data ?? []).map(rowToPost);
}

/**
 * 특정 쓰레드 메시지에서 생성된 위키 페이지들 (해당 채널의 위키 보드 한정).
 * 메시지/쓰레드 구분은 호출측에서 metadata.source_scope ('message' | 'thread') 로 분류.
 */
export async function checkExistingWikiPagesByThread(threadMessageId: string, channelId: string): Promise<Post[]> {
  const sb = getClient();
  const wikiBoard = await fetchWikiBoardForChannel(channelId);
  if (!wikiBoard) return [];

  const { data, error } = await sb
    .from('posts')
    .select('*')
    .eq('board_id', wikiBoard.id)
    .eq('metadata->>source_thread_id', threadMessageId);

  if (error) {
    console.warn('[BoardService] 중복 위키 검사 실패:', error.message);
    return [];
  }
  return (data ?? []).map(rowToPost);
}

/**
 * 태스크 상태 변경 (+ 상태 변경 이력 기록)
 *
 * 채널 멤버 누구나 호출 가능 — 책임 추적은 게이팅이 아니라 status_history 의 변경자(by) 로 담보한다.
 * @param by    변경 주체 (히스토리에 기록). 미지정 시 이력만 생략.
 * @param note  반려 사유 등 (선택)
 */
export async function updateTaskStatus(
  postId: string,
  currentMetadata: Record<string, any> | null,
  status: string,
  by?: PostAuthor,
  note?: string | null,
): Promise<Post> {
  const now = new Date().toISOString();
  const from = (currentMetadata?.status as TaskStatus) ?? null;
  const newMeta: Record<string, any> = { ...(currentMetadata ?? {}), status, last_activity_at: now };
  if (by) {
    newMeta.status_history = appendStatusChange(currentMetadata, from, status as TaskStatus, by, now, note);
  }
  return updatePost(postId, { metadata: newMeta });
}
/** @deprecated updateInsightStatus → updateTaskStatus */
export const updateInsightStatus = updateTaskStatus;

/** 태스크 담당자 변경 */
export async function updateTaskAssignee(
  postId: string,
  currentMetadata: Record<string, any> | null,
  assignee: { uid: string; name: string; photoUrl?: string | null } | null,
): Promise<Post> {
  const now = new Date().toISOString();
  const newMeta = { ...(currentMetadata ?? {}), assignee, last_activity_at: now };
  return updatePost(postId, { metadata: newMeta });
}
/** @deprecated updateInsightAssignee → updateTaskAssignee */
export const updateInsightAssignee = updateTaskAssignee;

/** 태스크 마감일 변경 */
export async function updateTaskDueDate(
  postId: string,
  currentMetadata: Record<string, any> | null,
  dueDate: string | null,
): Promise<Post> {
  const now = new Date().toISOString();
  const newMeta = { ...(currentMetadata ?? {}), due_date: dueDate, last_activity_at: now };
  return updatePost(postId, { metadata: newMeta });
}
/** @deprecated updateInsightDueDate → updateTaskDueDate */
export const updateInsightDueDate = updateTaskDueDate;

/** 태스크 완료 처리 (기록 저장 없이 상태만 변경) */
export async function completeTaskOnly(
  postId: string,
  currentMetadata: Record<string, any> | null,
  by?: PostAuthor,
): Promise<Post> {
  const now = new Date().toISOString();
  const from = (currentMetadata?.status as TaskStatus) ?? null;
  const newMeta: Record<string, any> = { ...(currentMetadata ?? {}), status: 'done', last_activity_at: now };
  if (by) {
    newMeta.status_history = appendStatusChange(currentMetadata, from, 'done', by, now);
  }
  return updatePost(postId, { metadata: newMeta });
}
/** @deprecated completeInsightOnly → completeTaskOnly */
export const completeInsightOnly = completeTaskOnly;

/**
 * 태스크 완료 + 완료 문서 저장 (단일 보드 통합 방식)
 *
 * 별도 records 보드/포스트를 만들지 않고, 원본 태스크의 metadata.completion에
 * AI가 생성한 완료 문서(요약/카테고리/태그/핵심결정/결과/참여자/기간)를 병합한다.
 * 태스크의 title/plain_text/tags도 사용자가 다듬은 최종 값으로 업데이트한다.
 */
export async function completeTaskCard(
  taskPost: Post,
  completionData: {
    title: string;
    summary: string;
    category: string;
    tags: string[];
    keyDecisions: string[];
    outcome: string;
    participants: Array<{ uid: string; name: string; photoUrl: string | null }>;
  },
  by?: PostAuthor,
): Promise<Post> {
  const now = new Date().toISOString();
  const taskMeta = taskPost.metadata ?? {};
  const from = (taskMeta.status as TaskStatus) ?? null;

  const completion = {
    summary: completionData.summary,
    category: completionData.category || null,
    tags: completionData.tags,
    key_decisions: completionData.keyDecisions,
    outcome: completionData.outcome,
    participants: completionData.participants,
    period_start: taskPost.created_at,
    period_end: now,
  };

  return updatePost(taskPost.id, {
    title: completionData.title,
    plain_text: completionData.summary,
    tags: completionData.tags,
    metadata: {
      ...taskMeta,
      status: 'done',
      completion,
      ...(by ? { status_history: appendStatusChange(taskMeta, from, 'done', by, now) } : {}),
      last_activity_at: now,
    },
  });
}

// ═══════════════════════════════
//  Tasks — 워칭(Watching)
// ═══════════════════════════════

/** 태스크 워칭 토글 */
export async function toggleTaskWatcher(
  postId: string,
  currentMetadata: Record<string, any> | null,
  uid: string,
): Promise<Post> {
  const now = new Date().toISOString();
  const watchers: string[] = currentMetadata?.watchers ?? [];
  const isWatching = watchers.includes(uid);

  const newWatchers = isWatching
    ? watchers.filter((w: string) => w !== uid)
    : [...watchers, uid];

  const newMeta = {
    ...(currentMetadata ?? {}),
    watchers: newWatchers,
    last_activity_at: now,
  };

  return updatePost(postId, { metadata: newMeta });
}
/** @deprecated toggleInsightWatcher → toggleTaskWatcher */
export const toggleInsightWatcher = toggleTaskWatcher;

/**
 * 워쳐 자동 추가 (댓글 작성/담당자 변경 시 호출)
 *
 * 이미 워쳐인 경우 중복 추가하지 않습니다.
 */
export async function addTaskWatcher(
  postId: string,
  currentMetadata: Record<string, any> | null,
  uid: string,
): Promise<Post> {
  const watchers: string[] = currentMetadata?.watchers ?? [];
  if (watchers.includes(uid)) {
    // 이미 워쳐 → 업데이트 불필요
    const sb = getClient();
    const { data, error } = await sb.from('posts').select('*').eq('id', postId).single();
    if (error) throw error;
    return rowToPost(data);
  }

  const now = new Date().toISOString();
  const newMeta = {
    ...(currentMetadata ?? {}),
    watchers: [...watchers, uid],
    last_activity_at: now,
  };

  return updatePost(postId, { metadata: newMeta });
}
/** @deprecated addInsightWatcher → addTaskWatcher */
export const addInsightWatcher = addTaskWatcher;

// ═══════════════════════════════
//  통합 알림 설정 (master / 댓글 / 새 글 토글)
// ═══════════════════════════════

export interface BoardNotifyPrefs {
  /** 마스터 ON/OFF — OFF 면 어떤 알림도 발송 안 됨 */
  notifications_enabled: boolean;
  /** 내가 참여한 글의 댓글 알림 */
  notify_comment: boolean;
  /** 새 글 작성 알림 */
  notify_new_post: boolean;
}

/** 기본값 — 마스터 ON, 댓글 ON, 새 글 OFF */
export const DEFAULT_BOARD_NOTIFY_PREFS: BoardNotifyPrefs = {
  notifications_enabled: true,
  notify_comment: true,
  notify_new_post: false,
};

/** 여러 게시판의 통합 알림 설정 일괄 조회 (미설정 → 기본값) */
export async function getBoardNotifyPrefsMap(
  boardIds: string[],
  userId: string,
): Promise<Record<string, BoardNotifyPrefs>> {
  if (boardIds.length === 0) return {};
  const sb = getClient();
  const { data } = await sb
    .from('board_notify_settings')
    .select('board_id, notifications_enabled, notify_comment, notify_new_post')
    .in('board_id', boardIds)
    .eq('user_id', userId);

  const result: Record<string, BoardNotifyPrefs> = {};
  for (const id of boardIds) result[id] = { ...DEFAULT_BOARD_NOTIFY_PREFS };
  for (const row of data ?? []) {
    result[row.board_id] = {
      notifications_enabled: row.notifications_enabled ?? DEFAULT_BOARD_NOTIFY_PREFS.notifications_enabled,
      notify_comment:        row.notify_comment        ?? DEFAULT_BOARD_NOTIFY_PREFS.notify_comment,
      notify_new_post:       row.notify_new_post       ?? DEFAULT_BOARD_NOTIFY_PREFS.notify_new_post,
    };
  }
  return result;
}

/** 게시판 통합 알림 설정 일부 / 전체 갱신 (upsert) */
export async function setBoardNotifyPrefs(
  boardId: string,
  userId: string,
  prefs: Partial<BoardNotifyPrefs>,
): Promise<void> {
  const sb = getClient();
  // 기존 row 가 없을 때를 위해 모자란 필드를 default 로 채워 upsert
  const { data: existing } = await sb
    .from('board_notify_settings')
    .select('notifications_enabled, notify_comment, notify_new_post')
    .eq('board_id', boardId)
    .eq('user_id', userId)
    .maybeSingle();
  const merged: BoardNotifyPrefs = {
    notifications_enabled: prefs.notifications_enabled ?? existing?.notifications_enabled ?? DEFAULT_BOARD_NOTIFY_PREFS.notifications_enabled,
    notify_comment:        prefs.notify_comment        ?? existing?.notify_comment        ?? DEFAULT_BOARD_NOTIFY_PREFS.notify_comment,
    notify_new_post:       prefs.notify_new_post       ?? existing?.notify_new_post       ?? DEFAULT_BOARD_NOTIFY_PREFS.notify_new_post,
  };
  const { error } = await sb
    .from('board_notify_settings')
    .upsert(
      {
        board_id: boardId,
        user_id: userId,
        ...merged,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,board_id' },
    );
  if (error) throw error;
}

// ═══════════════════════════════
//  Board Reads (게시판 미읽음)
// ═══════════════════════════════

/** 게시판 마지막 읽은 시점 업데이트 (upsert) */
export async function markBoardRead(boardId: string, userId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('user_board_reads')
    .upsert(
      { board_id: boardId, user_id: userId, last_read_at: new Date().toISOString() },
      { onConflict: 'user_id,board_id' },
    );

  if (error) console.warn('[BoardService] markBoardRead 실패:', error);
}

/** 여러 게시판의 마지막 읽은 시점 일괄 조회 */
export async function getBoardReadTimes(
  boardIds: string[],
  userId: string,
): Promise<Record<string, string>> {
  if (boardIds.length === 0) return {};
  const sb = getClient();
  const { data } = await sb
    .from('user_board_reads')
    .select('board_id, last_read_at')
    .eq('user_id', userId)
    .in('board_id', boardIds);

  const result: Record<string, string> = {};
  for (const row of data ?? []) {
    result[row.board_id] = row.last_read_at ?? '';
  }
  return result;
}

// ═══════════════════════════════
//  Post Comment Reads (댓글 미읽음)
// ═══════════════════════════════

/** 게시글 댓글 마지막 읽은 시점 업데이트 (upsert) */
export async function markPostCommentRead(postId: string, userId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('user_post_comment_reads')
    .upsert(
      { post_id: postId, user_id: userId, last_read_at: new Date().toISOString() },
      { onConflict: 'user_id,post_id' },
    );

  if (error) console.warn('[BoardService] markPostCommentRead 실패:', error);
}

/** 여러 게시글의 댓글 마지막 읽은 시점 일괄 조회 */
export async function getPostCommentReadTimes(
  postIds: string[],
  userId: string,
): Promise<Record<string, string>> {
  if (postIds.length === 0) return {};
  const sb = getClient();
  const { data } = await sb
    .from('user_post_comment_reads')
    .select('post_id, last_read_at')
    .in('post_id', postIds)
    .eq('user_id', userId);

  const result: Record<string, string> = {};
  for (const row of data ?? []) {
    result[row.post_id] = row.last_read_at ?? '';
  }
  return result;
}

/**
 * 미읽음 댓글이 있는 게시글 ID Set 계산
 *
 * 1) 해당 게시글들의 마지막 댓글 작성 시점(max(created_at)) 조회
 * 2) 사용자의 마지막 읽은 시점(last_read_at) 조회
 * 3) 읽지 않았거나 읽은 이후 새 댓글이 있으면 미읽음 처리
 */
export async function getUnreadCommentPostIds(
  postIds: string[],
  userId: string,
): Promise<Set<string>> {
  if (postIds.length === 0 || !userId) return new Set();
  const sb = getClient();

  // 1) 각 게시글의 마지막 댓글 작성 시점 조회
  const { data: commentData } = await sb
    .from('comments')
    .select('target_id, created_at')
    .eq('target_type', 'post')
    .in('target_id', postIds)
    .order('created_at', { ascending: false });

  // 게시글별 최신 댓글 시점 맵
  const latestCommentMap: Record<string, string> = {};
  for (const row of commentData ?? []) {
    if (!latestCommentMap[row.target_id]) {
      latestCommentMap[row.target_id] = row.created_at ?? '';
    }
  }

  // 댓글이 없는 게시글은 미읽음 대상에서 제외
  const postIdsWithComments = Object.keys(latestCommentMap);
  if (postIdsWithComments.length === 0) return new Set();

  // 2) 사용자의 마지막 읽은 시점 조회
  const readTimes = await getPostCommentReadTimes(postIdsWithComments, userId);

  // 3) 비교: 읽지 않았거나 읽은 이후 새 댓글이 있으면 미읽음
  const unread = new Set<string>();
  for (const postId of postIdsWithComments) {
    const lastComment = latestCommentMap[postId];
    const lastRead = readTimes[postId];
    if (!lastRead || new Date(lastComment) > new Date(lastRead)) {
      unread.add(postId);
    }
  }
  return unread;
}

// ═══════════════════════════════
//  Post Read Receipts (열람 추적)
// ═══════════════════════════════

/** 게시글 열람 기록 (upsert) — 멘션된 게시글 열람 시 호출 */
export async function recordPostRead(postId: string, userId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('post_read_receipts')
    .upsert(
      { post_id: postId, user_id: userId, read_at: new Date().toISOString() },
      { onConflict: 'post_id,user_id' },
    );

  if (error) console.warn('[BoardService] recordPostRead 실패:', error);
}

/** 게시글의 열람 기록 조회 (확인 현황 UI용) */
export async function getPostReadReceipts(
  postId: string,
): Promise<{ user_id: string; read_at: string }[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('post_read_receipts')
    .select('user_id, read_at')
    .eq('post_id', postId)
    .order('read_at', { ascending: true });

  if (error) {
    console.warn('[BoardService] getPostReadReceipts 실패:', error);
    return [];
  }
  return (data ?? []).map((r) => ({ user_id: r.user_id, read_at: r.read_at ?? '' }));
}

// ═══════════════════════════════
//  Comment Read Receipts (댓글 열람 추적)
// ═══════════════════════════════

/** 댓글 열람 기록 (upsert) — 멘션된 댓글 열람 시 호출 */
export async function recordCommentRead(commentId: string, userId: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb
    .from('comment_read_receipts')
    .upsert(
      { comment_id: commentId, user_id: userId, read_at: new Date().toISOString() },
      { onConflict: 'comment_id,user_id' },
    );

  if (error) console.warn('[BoardService] recordCommentRead 실패:', error);
}

/** 여러 댓글의 열람 기록 일괄 조회 (확인 현황 UI용) */
export async function getCommentReadReceiptsBatch(
  commentIds: string[],
): Promise<Record<string, { user_id: string; read_at: string }[]>> {
  if (commentIds.length === 0) return {};
  const sb = getClient();
  const { data, error } = await sb
    .from('comment_read_receipts')
    .select('comment_id, user_id, read_at')
    .in('comment_id', commentIds)
    .order('read_at', { ascending: true });

  if (error) {
    console.warn('[BoardService] getCommentReadReceiptsBatch 실패:', error);
    return {};
  }

  const grouped: Record<string, { user_id: string; read_at: string }[]> = {};
  for (const row of data ?? []) {
    const id = row.comment_id as string;
    (grouped[id] ??= []).push({ user_id: row.user_id, read_at: row.read_at });
  }
  return grouped;
}

// ═══════════════════════════════
//  Dashboard — 워크스페이스 전체 활동 피드
// ═══════════════════════════════

/** Dashboard 표시용 — 게시글 + 보드 컨텍스트 */
export interface DashboardPostItem {
  post: Post;
  boardId: string;
  boardName: string;
  boardType: string;
}

/** Dashboard 표시용 — 댓글 + 게시글 + 보드 컨텍스트 */
export interface DashboardCommentItem {
  comment: Comment;
  postId: string;
  postTitle: string;
  boardId: string;
  boardName: string;
}

/** sinceDays 일 만큼 거슬러 ISO timestamp */
function sinceIso(days: number): string {
  return new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
}

/** 워크스페이스 전체 최근 게시글 (기간 제한, 보드 정보 함께) */
/** 워크스페이스 전체 최근 게시글 — limit + offset 페이지네이션 (날짜 제한 없음).
 *  더보기 클릭 시 offset 늘려 추가 호출. */
export async function fetchRecentPostsForWorkspace(
  workspaceId: string,
  options?: { limit?: number; offset?: number; boardIds?: string[]; workspaceScopeOnly?: boolean },
): Promise<DashboardPostItem[]> {
  const sb = getClient();
  const limit = options?.limit ?? 20;
  const offset = options?.offset ?? 0;
  const boardIds = options?.boardIds;

  // boardIds 가 빈 배열이면 결과도 빈 배열 — 쿼리 자체 skip (PostgREST in.() 빈 배열은 invalid)
  if (boardIds && boardIds.length === 0) return [];

  let query = sb
    .from('posts')
    .select('*, boards!inner(id, name, type, workspace_id, scope)')
    .eq('boards.workspace_id', workspaceId);
  // 워크스페이스 대시보드: 워크스페이스 스코프 게시판만 (채널 게시판 제외)
  if (options?.workspaceScopeOnly) {
    query = query.eq('boards.scope', 'workspace');
  }
  if (boardIds && boardIds.length > 0) {
    query = query.in('board_id', boardIds);
  }
  const { data, error } = await query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw error;
  return (data ?? []).map(row => ({
    post: rowToPost(row),
    boardId: row.boards.id,
    boardName: row.boards.name,
    boardType: row.boards.type,
  }));
}

/** [내부] 워크스페이스의 모든 post id 목록 조회 — comments 와 post 사이에 FK 가 없어 in() 필터로 우회 */
async function listWorkspacePostIds(workspaceId: string, boardIds?: string[], workspaceScopeOnly?: boolean): Promise<string[]> {
  const sb = getClient();
  let q = workspaceScopeOnly
    ? sb.from('posts').select('id, boards!inner(scope)').eq('workspace_id', workspaceId).eq('boards.scope', 'workspace')
    : sb.from('posts').select('id').eq('workspace_id', workspaceId);
  if (boardIds && boardIds.length > 0) q = q.in('board_id', boardIds);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map(r => r.id as string);
}

/** [내부] post id 들의 컨텍스트(제목 + 보드 id/name) 조회 — comments 후속 enrich */
async function fetchPostContextByIds(
  postIds: string[],
): Promise<Map<string, { id: string; title: string; board_id: string; boardName: string }>> {
  const m = new Map<string, { id: string; title: string; board_id: string; boardName: string }>();
  if (postIds.length === 0) return m;
  const sb = getClient();
  const { data, error } = await sb
    .from('posts')
    .select('id, title, board_id, boards!inner(id, name)')
    .in('id', postIds);
  if (error) throw error;
  for (const p of (data ?? []) as any[]) {
    m.set(p.id, { id: p.id, title: p.title, board_id: p.board_id, boardName: p.boards?.name ?? '' });
  }
  return m;
}

/** 워크스페이스 전체 최근 댓글 (기간 제한 없이 limit 만, 게시글·보드 정보 함께)
 *  boardIds 옵션 — 즐겨찾기 보드 필터 등 특정 보드에 속한 게시글의 댓글만. */
export async function fetchRecentCommentsForWorkspace(
  workspaceId: string,
  options?: { limit?: number; boardIds?: string[]; workspaceScopeOnly?: boolean },
): Promise<DashboardCommentItem[]> {
  const sb = getClient();
  const limit = options?.limit ?? 50;
  const boardIds = options?.boardIds;

  if (boardIds && boardIds.length === 0) return [];

  // 워크스페이스의 post id 목록 (boardIds 지정 시 그 보드들에 속한 것만).
  // workspaceScopeOnly: 워크스페이스 스코프 게시판만 (채널 게시판 제외) — boards join 으로 필터.
  let postIdQuery = options?.workspaceScopeOnly
    ? sb.from('posts').select('id, boards!inner(scope)').eq('workspace_id', workspaceId).eq('boards.scope', 'workspace')
    : sb.from('posts').select('id').eq('workspace_id', workspaceId);
  if (boardIds && boardIds.length > 0) postIdQuery = postIdQuery.in('board_id', boardIds);
  const { data: postIdRows, error: postIdErr } = await postIdQuery;
  if (postIdErr) throw postIdErr;
  const wsPostIds = (postIdRows ?? []).map(r => r.id as string);
  if (wsPostIds.length === 0) return [];

  const { data, error } = await sb
    .from('comments')
    .select('*')
    .eq('target_type', 'post')
    .in('target_id', wsPostIds)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;

  const rows = data ?? [];
  const targetIds = Array.from(new Set(rows.map(r => r.target_id as string)));
  const ctxMap = await fetchPostContextByIds(targetIds);
  const out: DashboardCommentItem[] = [];
  for (const row of rows) {
    const ctx = ctxMap.get(row.target_id as string);
    if (!ctx) continue;
    out.push({
      comment: rowToComment(row),
      postId: ctx.id,
      postTitle: ctx.title,
      boardId: ctx.board_id,
      boardName: ctx.boardName,
    });
  }
  return out;
}

/** "나에게" 피드용 항목 — 멘션된 게시글/댓글 + 내 댓글에 달린 답글
 *  kind 로 구분, mergedAt = 정렬 기준 시점 (created_at). */
export type MyFeedItem =
  | { kind: 'post-mention';    mergedAt: string; data: DashboardPostItem }
  | { kind: 'comment-mention'; mergedAt: string; data: DashboardCommentItem }
  | { kind: 'reply-to-mine';   mergedAt: string; data: DashboardCommentItem };

/** "나에게" 피드 — 멘션 + 내 댓글에 달린 답글 (최근 90일) */
export async function fetchMyFeed(
  workspaceId: string,
  userId: string,
  options?: { sinceDays?: number; limit?: number },
): Promise<MyFeedItem[]> {
  const sb = getClient();
  const since = sinceIso(options?.sinceDays ?? 90);
  const limit = options?.limit ?? 50;

  // (a) 나를 멘션한 게시글
  const postMentionsP = sb
    .from('posts')
    .select('*, boards!inner(id, name, type, workspace_id)')
    .eq('boards.workspace_id', workspaceId)
    .contains('mentioned_user_ids', [userId])
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(limit);

  // [내부] 워크스페이스 post id 미리 조회 (comments 필터용 — FK 없는 우회)
  const wsPostIdsP = listWorkspacePostIds(workspaceId);

  // (c) 내가 쓴 댓글 ID 목록 (since 이후)
  const myCommentsP = sb
    .from('comments')
    .select('id')
    .eq('author->>uid', userId)
    .gte('created_at', since)
    .limit(500);

  const [pm, wsPostIds, mc] = await Promise.all([postMentionsP, wsPostIdsP, myCommentsP]);
  if (pm.error) throw pm.error;
  if (mc.error) throw mc.error;

  // (b) 나를 멘션한 댓글 — target_id IN ws post ids
  let cmRows: any[] = [];
  if (wsPostIds.length > 0) {
    const { data, error } = await sb
      .from('comments')
      .select('*')
      .eq('target_type', 'post')
      .in('target_id', wsPostIds)
      .contains('mentioned_user_ids', [userId])
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    cmRows = data ?? [];
  }

  const myCommentIds = (mc.data ?? []).map(r => r.id as string);

  // (c) 내 댓글에 달린 답글 — wsPostIds + reply_to IN myCommentIds
  let repliesRows: any[] = [];
  if (myCommentIds.length > 0 && wsPostIds.length > 0) {
    const { data, error } = await sb
      .from('comments')
      .select('*')
      .eq('target_type', 'post')
      .in('target_id', wsPostIds)
      .in('reply_to_comment_id', myCommentIds)
      .neq('author->>uid', userId)
      .gte('created_at', since)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    repliesRows = data ?? [];
  }

  // 댓글 컨텍스트 일괄 enrich
  const allTargetIds = Array.from(new Set([
    ...cmRows.map(r => r.target_id as string),
    ...repliesRows.map(r => r.target_id as string),
  ]));
  const ctxMap = await fetchPostContextByIds(allTargetIds);

  const items: MyFeedItem[] = [];
  for (const row of pm.data ?? []) {
    items.push({
      kind: 'post-mention',
      mergedAt: row.created_at ?? '',
      data: { post: rowToPost(row), boardId: row.boards.id, boardName: row.boards.name, boardType: row.boards.type },
    });
  }
  for (const row of cmRows) {
    const ctx = ctxMap.get(row.target_id as string);
    if (!ctx) continue;
    items.push({
      kind: 'comment-mention',
      mergedAt: row.created_at,
      data: { comment: rowToComment(row), postId: ctx.id, postTitle: ctx.title, boardId: ctx.board_id, boardName: ctx.boardName },
    });
  }
  for (const row of repliesRows) {
    const ctx = ctxMap.get(row.target_id as string);
    if (!ctx) continue;
    items.push({
      kind: 'reply-to-mine',
      mergedAt: row.created_at,
      data: { comment: rowToComment(row), postId: ctx.id, postTitle: ctx.title, boardId: ctx.board_id, boardName: ctx.boardName },
    });
  }

  // 시간 역순 + dedupe by (kind, id)
  items.sort((a, b) => b.mergedAt.localeCompare(a.mergedAt));
  const seen = new Set<string>();
  const dedup: MyFeedItem[] = [];
  for (const it of items) {
    const id = it.kind === 'post-mention' ? `p:${it.data.post.id}` : `c:${it.data.comment.id}:${it.kind}`;
    if (seen.has(id)) continue;
    seen.add(id);
    dedup.push(it);
    if (dedup.length >= limit) break;
  }
  return dedup;
}

/** 워크스페이스 전체 통합 검색 결과 — 게시글 + 댓글 두 섹션 */
export interface WorkspaceSearchResults {
  posts: DashboardPostItem[];
  comments: DashboardCommentItem[];
}

/** 검색 대상 필드 — 다중 선택 가능. 모두 false 인 경우 검색 안 함. */
export interface SearchFields {
  /** 게시판 글 제목 */
  title?: boolean;
  /** 게시판 글 본문 */
  body?: boolean;
  /** 댓글 본문 */
  comments?: boolean;
  /** 작성자 이름 (게시글·댓글 모두) */
  author?: boolean;
}

/** 기본 검색 필드 — 제목만 ON */
export const DEFAULT_SEARCH_FIELDS: SearchFields = { title: true };

/** 워크스페이스 전체 통합 검색 — fields 에 따라 posts / comments 각각 필터 조합 */
export async function searchAcrossWorkspace(
  workspaceId: string,
  query: string,
  options?: { limit?: number; fields?: SearchFields; boardIds?: string[]; workspaceScopeOnly?: boolean },
): Promise<WorkspaceSearchResults> {
  const trimmed = query.trim();
  if (!trimmed) return { posts: [], comments: [] };
  const sb = getClient();
  const limit = options?.limit ?? 20;
  const boardIds = options?.boardIds;
  const pattern = `%${trimmed}%`;
  const fields = options?.fields ?? DEFAULT_SEARCH_FIELDS;
  const anyField = !!(fields.title || fields.body || fields.comments || fields.author);
  if (!anyField) return { posts: [], comments: [] };

  // posts 쿼리 — title / body / author 중 하나라도 선택된 경우만
  const needsPosts = !!(fields.title || fields.body || fields.author);
  const postsOrParts: string[] = [];
  if (fields.title)  postsOrParts.push(`title.ilike.${pattern}`);
  if (fields.body)   postsOrParts.push(`plain_text.ilike.${pattern}`);
  if (fields.author) postsOrParts.push(`author->>name.ilike.${pattern}`);

  let postsQuery: any = null;
  if (needsPosts) {
    postsQuery = sb
      .from('posts')
      .select('*, boards!inner(id, name, type, workspace_id, scope)')
      .eq('boards.workspace_id', workspaceId)
      .or(postsOrParts.join(','));
    if (options?.workspaceScopeOnly) postsQuery = postsQuery.eq('boards.scope', 'workspace');
    if (boardIds && boardIds.length > 0) postsQuery = postsQuery.in('board_id', boardIds);
    postsQuery = postsQuery.order('created_at', { ascending: false }).limit(limit);
  }
  const postsP = postsQuery ?? Promise.resolve({ data: [] as any[], error: null });

  // comments 쿼리 — comments / author 중 하나라도 선택된 경우만 (comments↔posts FK 없어 2-step)
  const needsComments = !!(fields.comments || fields.author);
  const commentsOrParts: string[] = [];
  if (fields.comments) commentsOrParts.push(`text.ilike.${pattern}`);
  if (fields.author)   commentsOrParts.push(`author->>name.ilike.${pattern}`);

  const [postsResult, wsPostIds] = await Promise.all([
    postsP,
    needsComments ? listWorkspacePostIds(workspaceId, boardIds, options?.workspaceScopeOnly) : Promise.resolve([] as string[]),
  ]);
  if ((postsResult as any).error) throw (postsResult as any).error;

  let commentRows: any[] = [];
  if (needsComments && wsPostIds.length > 0) {
    const { data, error } = await sb
      .from('comments')
      .select('*')
      .eq('target_type', 'post')
      .in('target_id', wsPostIds)
      .or(commentsOrParts.join(','))
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    commentRows = data ?? [];
  }

  const ctxMap = await fetchPostContextByIds(Array.from(new Set(commentRows.map(r => r.target_id as string))));

  return {
    posts: ((postsResult as any).data ?? []).map((row: any) => ({
      post: rowToPost(row),
      boardId: row.boards.id,
      boardName: row.boards.name,
      boardType: row.boards.type,
    })),
    comments: commentRows.flatMap((row: any) => {
      const ctx = ctxMap.get(row.target_id as string);
      if (!ctx) return [];
      return [{
        comment: rowToComment(row),
        postId: ctx.id,
        postTitle: ctx.title,
        boardId: ctx.board_id,
        boardName: ctx.boardName,
      }];
    }),
  };
}

/** 참여중인 토론 항목 — 게시글 + 보드 정보 + 마지막 활동 시각 + 내 관여 종류 */
export interface ActiveThreadItem {
  post: Post;
  boardId: string;
  boardName: string;
  boardType: string;
  /** 가장 최근 댓글 시각 */
  lastActivityAt: string;
  /** 'author' = 내가 작성한 글, 'commenter' = 내가 댓글로 참여 */
  myInvolvement: 'author' | 'commenter';
}

/** 참여중인 게시글들의 댓글 — DashboardCommentItem 에 isMine 추가 */
export interface ParticipatingCommentItem extends DashboardCommentItem {
  /** 이 댓글이 내가 단 댓글인지 — UI 강조용 */
  isMine: boolean;
}

/**
 * 참여중인 게시글들의 모든 댓글 — 내가 작성했거나 댓글을 단 게시글에 달린 댓글.
 *
 * 1) 내가 댓글 단 게시글 ID + 내가 작성한 게시글 ID 수집 (워크스페이스 내)
 * 2) 그 게시글들의 모든 댓글 조회 (최신순, limit cap)
 * 3) 각 댓글에 isMine 플래그 부여 + post 컨텍스트 채움
 */
export async function fetchParticipatingComments(
  workspaceId: string,
  userId: string,
  options?: { limit?: number },
): Promise<ParticipatingCommentItem[]> {
  const sb = getClient();
  const limit = options?.limit ?? 20;

  // 1) 내가 댓글 단 post id 들
  const { data: myComments, error: e1 } = await sb
    .from('comments')
    .select('target_id')
    .eq('author->>uid', userId)
    .eq('target_type', 'post')
    .limit(500);
  if (e1) throw e1;
  const commentedIds = new Set((myComments ?? []).map(r => r.target_id as string));

  // 2) 내가 작성한 post id 들 (워크스페이스 내)
  const { data: authored, error: e2 } = await sb
    .from('posts')
    .select('id, boards!inner(workspace_id)')
    .eq('boards.workspace_id', workspaceId)
    .eq('author->>uid', userId)
    .limit(500);
  if (e2) throw e2;
  const authoredIds = new Set((authored ?? []).map(r => r.id as string));

  // 워크스페이스 안의 참여 게시글로 좁히기 위해 commentedIds 도 워크스페이스 필터링 필요.
  // post.workspace_id 직접 확인 — comments → posts.workspace_id 가 워크스페이스 안인 것만.
  const allInvolvedIds = Array.from(new Set<string>([...commentedIds, ...authoredIds]));
  if (allInvolvedIds.length === 0) return [];

  // 3) 워크스페이스 검증 + 보드 정보 fetch
  const { data: validPosts, error: e3 } = await sb
    .from('posts')
    .select('id, boards!inner(workspace_id)')
    .in('id', allInvolvedIds)
    .eq('boards.workspace_id', workspaceId);
  if (e3) throw e3;
  const validIds = (validPosts ?? []).map(r => r.id as string);
  if (validIds.length === 0) return [];

  // 4) 그 글들의 모든 댓글 (최신순, limit cap)
  const { data: rows, error: e4 } = await sb
    .from('comments')
    .select('*')
    .eq('target_type', 'post')
    .in('target_id', validIds)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (e4) throw e4;

  const targetIds = Array.from(new Set((rows ?? []).map(r => r.target_id as string)));
  const ctxMap = await fetchPostContextByIds(targetIds);
  const out: ParticipatingCommentItem[] = [];
  for (const row of rows ?? []) {
    const ctx = ctxMap.get(row.target_id as string);
    if (!ctx) continue;
    const c = rowToComment(row);
    out.push({
      comment: c,
      postId: ctx.id,
      postTitle: ctx.title,
      boardId: ctx.board_id,
      boardName: ctx.boardName,
      isMine: c.author?.uid === userId,
    });
  }
  return out;
}

/**
 * @deprecated fetchActiveThreadsForMe → 게시글 기반 (구). 댓글 기반은 fetchParticipatingComments 사용.
 *
 * 참여중인 토론 — 내가 작성했거나 댓글을 단 게시글 중 댓글 활동이 있는 것.
 */
export async function fetchActiveThreadsForMe(
  workspaceId: string,
  userId: string,
  options?: { limit?: number },
): Promise<ActiveThreadItem[]> {
  const sb = getClient();
  const limit = options?.limit ?? 20;

  // 1) 내가 댓글 단 post id 들
  const { data: myComments, error: e1 } = await sb
    .from('comments')
    .select('target_id')
    .eq('author->>uid', userId)
    .eq('target_type', 'post')
    .limit(500);
  if (e1) throw e1;
  const commentedIds = new Set((myComments ?? []).map(r => r.target_id as string));

  // 2) 내가 작성한 post id 들 (워크스페이스 내)
  const { data: authored, error: e2 } = await sb
    .from('posts')
    .select('id, boards!inner(workspace_id)')
    .eq('boards.workspace_id', workspaceId)
    .eq('author->>uid', userId)
    .limit(500);
  if (e2) throw e2;
  const authoredIds = new Set((authored ?? []).map(r => r.id as string));

  const allInvolvedIds = new Set<string>([...commentedIds, ...authoredIds]);
  if (allInvolvedIds.size === 0) return [];

  // 3) 그 글들의 최근 댓글 (기간 제한 없이 모든 댓글 — 활동순으로 정렬 후 cap)
  const involvedArr = Array.from(allInvolvedIds);
  const { data: recentComments, error: e3 } = await sb
    .from('comments')
    .select('target_id, created_at')
    .in('target_id', involvedArr)
    .order('created_at', { ascending: false });
  if (e3) throw e3;

  const latestByPost = new Map<string, string>();
  for (const row of recentComments ?? []) {
    if (!latestByPost.has(row.target_id as string)) {
      latestByPost.set(row.target_id as string, row.created_at as string);
    }
  }
  if (latestByPost.size === 0) return [];

  // 4) 활동이 있는 글의 본문 + 보드 정보
  const activeIds = Array.from(latestByPost.keys());
  const { data: postsData, error: e4 } = await sb
    .from('posts')
    .select('*, boards!inner(id, name, type, workspace_id)')
    .eq('boards.workspace_id', workspaceId)
    .in('id', activeIds);
  if (e4) throw e4;

  const items: ActiveThreadItem[] = (postsData ?? []).map(row => ({
    post: rowToPost(row),
    boardId: row.boards.id,
    boardName: row.boards.name,
    boardType: row.boards.type,
    lastActivityAt: latestByPost.get(row.id as string) ?? '',
    myInvolvement: authoredIds.has(row.id as string) ? 'author' : 'commenter',
  }));

  items.sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
  return items.slice(0, limit);
}

// ═══════════════════════════════
//  Board Categories — 게시판 그룹화
// ═══════════════════════════════

/** 워크스페이스의 카테고리 목록 (sort_order asc) */
export async function fetchBoardCategories(workspaceId: string): Promise<BoardCategory[]> {
  const sb = getClient();
  const { data, error } = await sb
    .from('board_categories')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []).map(rowToBoardCategory);
}

/** 카테고리 생성 — 끝에 추가 (max sort_order + 1) */
export async function createBoardCategory(workspaceId: string, name: string): Promise<BoardCategory> {
  const trimmed = name.trim();
  if (!trimmed) throw new Error('카테고리 이름이 비어 있습니다');
  const sb = getClient();
  // 현재 최대 sort_order 조회
  const { data: maxRow } = await sb
    .from('board_categories')
    .select('sort_order')
    .eq('workspace_id', workspaceId)
    .order('sort_order', { ascending: false })
    .limit(1)
    .maybeSingle();
  const nextOrder = (maxRow?.sort_order ?? -1) + 1;
  const { data, error } = await sb
    .from('board_categories')
    .insert({ workspace_id: workspaceId, name: trimmed, sort_order: nextOrder })
    .select()
    .single();
  if (error) throw error;
  return rowToBoardCategory(data);
}

/** 카테고리 이름/순서 수정 */
export async function updateBoardCategory(
  id: string,
  updates: { name?: string; sort_order?: number },
): Promise<BoardCategory> {
  const patch: TablesUpdate<'board_categories'> = { updated_at: new Date().toISOString() };
  if (updates.name !== undefined) patch.name = updates.name.trim();
  if (updates.sort_order !== undefined) patch.sort_order = updates.sort_order;
  const sb = getClient();
  const { data, error } = await sb
    .from('board_categories')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return rowToBoardCategory(data);
}

/** 카테고리 삭제 — boards.category_id 는 ON DELETE SET NULL 로 미지정으로 떨어짐 */
export async function deleteBoardCategory(id: string): Promise<void> {
  const sb = getClient();
  const { error } = await sb.from('board_categories').delete().eq('id', id);
  if (error) throw error;
}

/** 보드의 카테고리 변경 (null = 미지정) — 옮긴 카테고리 내 끝에 배치 */
export async function setBoardCategory(boardId: string, categoryId: string | null): Promise<void> {
  const sb = getClient();
  // 새 카테고리 내 max sort_order + 1
  let nextOrder = 0;
  if (categoryId !== null) {
    const { data: maxRow } = await sb
      .from('boards')
      .select('category_sort_order')
      .eq('category_id', categoryId)
      .order('category_sort_order', { ascending: false })
      .limit(1)
      .maybeSingle();
    nextOrder = (maxRow?.category_sort_order ?? -1) + 1;
  } else {
    // 미지정 그룹 — workspace_id 의 category_id IS NULL 인 보드들의 max + 1
    // (미지정 안에서도 순서 유지 위해)
    const { data: cur } = await sb.from('boards').select('workspace_id').eq('id', boardId).maybeSingle();
    if (cur?.workspace_id) {
      const { data: maxRow } = await sb
        .from('boards')
        .select('category_sort_order')
        .eq('workspace_id', cur.workspace_id)
        .is('category_id', null)
        .order('category_sort_order', { ascending: false })
        .limit(1)
        .maybeSingle();
      nextOrder = (maxRow?.category_sort_order ?? -1) + 1;
    }
  }
  const { error } = await sb
    .from('boards')
    .update({ category_id: categoryId, category_sort_order: nextOrder })
    .eq('id', boardId);
  if (error) throw error;
}

/** 카테고리 자체 순서 일괄 갱신 — orderedIds 순서대로 0,1,2,... */
export async function reorderBoardCategories(orderedIds: string[]): Promise<void> {
  if (orderedIds.length === 0) return;
  const sb = getClient();
  // 개별 update 병렬 — 작은 데이터셋이라 충분
  await Promise.all(orderedIds.map((id, idx) =>
    sb.from('board_categories').update({ sort_order: idx, updated_at: new Date().toISOString() }).eq('id', id)
  ));
}

/** 카테고리 내 보드 순서 일괄 갱신 */
export async function reorderBoardsInCategory(orderedBoardIds: string[]): Promise<void> {
  if (orderedBoardIds.length === 0) return;
  const sb = getClient();
  await Promise.all(orderedBoardIds.map((id, idx) =>
    sb.from('boards').update({ category_sort_order: idx }).eq('id', id)
  ));
}
